// useTalentSearch Hook
// Manages talent discovery search functionality

import { useState, useCallback, useEffect, useRef } from 'react';
import talentDiscoveryService from '../../services/playerProfile/talentDiscoveryService';
import { useNotification } from '../../context/NotificationContext';

const DEBOUNCE_DELAY = 500;

export const useTalentSearch = (initialFilters = {}) => {
  const { showError } = useNotification();
  
  // Search state
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  // Filters state - aligned with backend API and player profile structure
  const [filters, setFilters] = useState({
    sport: '',           // athleticProfile.primarySport
    position: '',        // athleticProfile.positions[].position
    location: null,      // location.city/state/country
    ageRange: { min: 0, max: 90 },  // Calculated from dateOfBirth
    availability: [],    // availability.status array
    minSkillLevel: null, // skills average level (1-10)
    performance: {},     // athleticProfile.performanceMetrics
    q: '',              // Text search across name, headline, bio
    ...initialFilters
  });
  
  // Sort state
  const [sortBy, setSortBy] = useState('relevance');
  
  // Debounce timer ref
  const searchTimerRef = useRef(null);

  // Perform search
  const performSearch = useCallback(async (searchFilters = filters, page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await talentDiscoveryService.searchPlayers(
        searchFilters,
        {
          page,
          limit: pagination.limit,
          sortBy
        }
      );
      
      setProfiles(response.profiles || []);
      setPagination(response.pagination || { page, limit: pagination.limit, total: 0, pages: 0 });
      
      return response;
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while searching';
      setError(errorMessage);
      
      // Only show error notification if it's not an authentication error (401)
      // (the page will handle auth errors by showing login prompt)
      if (err.status !== 401) {
        showError(`Search failed: ${errorMessage}`);
      }
      
      // Set empty results on error
      setProfiles([]);
      setPagination({ page, limit: pagination.limit, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, sortBy, showError]);

  // Debounced search
  const debouncedSearch = useCallback((newFilters) => {
    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    // Set new timer
    searchTimerRef.current = setTimeout(() => {
      performSearch(newFilters, 1);
    }, DEBOUNCE_DELAY);
  }, [performSearch]);

  // Update filters
  const updateFilters = useCallback((updates) => {
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);
    
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Perform debounced search
    debouncedSearch(newFilters);
  }, [filters, debouncedSearch]);

  // Update single filter
  const updateFilter = useCallback((key, value) => {
    updateFilters({ [key]: value });
  }, [updateFilters]);

  // Clear filters - reset to defaults
  const clearFilters = useCallback(() => {
    const clearedFilters = {
      sport: '',
      position: '',
      location: null,
      ageRange: { min: 0, max: 90 },
      availability: [],
      minSkillLevel: null,
      performance: {},
      q: ''
    };
    setFilters(clearedFilters);
    performSearch(clearedFilters, 1);
  }, [performSearch]);

  // Change page
  const changePage = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }));
    performSearch(filters, page);
  }, [filters, performSearch]);

  // Change sort
  const changeSort = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    setPagination(prev => ({ ...prev, page: 1 }));
    performSearch(filters, 1);
  }, [filters, performSearch]);

  // Load more (for infinite scroll)
  const loadMore = useCallback(() => {
    if (pagination.page < pagination.pages && !loading) {
      const nextPage = pagination.page + 1;
      performSearch(filters, nextPage).then(response => {
        if (response) {
          setProfiles(prev => [...prev, ...response.profiles]);
          setPagination(response.pagination);
        }
      });
    }
  }, [filters, pagination.page, pagination.pages, loading, performSearch]);

  // Get filter suggestions
  const getFilterSuggestions = useCallback(async (filterType) => {
    // This could call an API endpoint to get dynamic suggestions
    // For now, return static suggestions
    const suggestions = {
      sport: ['Soccer', 'Basketball', 'Baseball', 'Tennis', 'Swimming'],
      position: {
        Soccer: ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'],
        Basketball: ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
        Baseball: ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Shortstop', 'Outfield']
      },
      preferredLevels: ['Professional', 'Semi-Professional', 'College', 'High School', 'Youth']
    };
    
    return suggestions[filterType] || [];
  }, []);

  // Save search
  const saveSearch = useCallback(async (name) => {
    try {
      // This would save the current search filters to backend
      // For now, save to localStorage
      const savedSearches = JSON.parse(localStorage.getItem('savedTalentSearches') || '[]');
      savedSearches.push({
        id: Date.now(),
        name,
        filters,
        sortBy,
        createdAt: new Date().toISOString()
      });
      localStorage.setItem('savedTalentSearches', JSON.stringify(savedSearches));
      
      return true;
    } catch (err) {
      showError('Failed to save search');
      return false;
    }
  }, [filters, sortBy, showError]);

  // Load saved search
  const loadSavedSearch = useCallback((searchId) => {
    try {
      const savedSearches = JSON.parse(localStorage.getItem('savedTalentSearches') || '[]');
      const search = savedSearches.find(s => s.id === searchId);
      
      if (search) {
        setFilters(search.filters);
        setSortBy(search.sortBy);
        performSearch(search.filters, 1);
        return true;
      }
      return false;
    } catch (err) {
      showError('Failed to load saved search');
      return false;
    }
  }, [performSearch, showError]);

  // Get saved searches
  const getSavedSearches = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem('savedTalentSearches') || '[]');
    } catch {
      return [];
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // Count active filters
  const countActiveFilters = useCallback(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') {
        // For ageRange, check if values differ from defaults
        if (key === 'ageRange') {
          return value.min !== 16 || value.max !== 35;
        }
        // For other objects, check if they have any keys
        return Object.keys(value).length > 0;
      }
      return true;
    }).length;
  }, [filters]);

  return {
    // State
    profiles,
    loading,
    error,
    pagination,
    filters,
    sortBy,
    
    // Methods
    performSearch,
    updateFilters,
    updateFilter,
    clearFilters,
    changePage,
    changeSort,
    loadMore,
    getFilterSuggestions,
    saveSearch,
    loadSavedSearch,
    getSavedSearches,
    
    // Computed
    hasFilters: countActiveFilters() > 0,
    activeFilterCount: countActiveFilters(),
    totalResults: pagination.total
  };
};
